const axios = require('axios');
const axiosRetry = require('axios-retry');

/**
 * @typedef ThrottleOptions
 * @prop {number} maxRequests
 */

/**
 * Adds retry logic to the given Axios instance.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 */
function retry(axiosInstance) {
  axiosRetry(axiosInstance, {
    retries: 3,
    shouldResetTimeout: true,
    retryDelay: (retryCount) => {
      // estimated GAE boot time is 15s
      const exponent = [1, 3, 4][retryCount - 1];
      return 2 ** exponent * 1000;
    },
    retryCondition: (error) => {
      if (error?.response?.status && [400, 401, 403, 404].includes(error.response.status)) {
        return false;
      }
      return true;
    },
  });
}

/**
 * Adds throttling to the given axios instance.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 * @param {RequestThrottleFunc} shouldThrottle
 * @param {ThrottleOptions} options
 */
function throttle(axiosInstance, shouldThrottle = () => true, options = { maxRequests: 5 }) {
  const INTERVAL_MS = 10;
  let pendingRequests = 0;

  const maxRequests = options?.maxRequests;

  // Taken from: https://gist.github.com/matthewsuan/2bdc9e7f459d5b073d58d1ebc0613169
  axiosInstance.interceptors.request.use(
    (config) =>
      new Promise((resolve) => {
        const interval = setInterval(() => {
          // Limit concurrent device requests
          if (pendingRequests < maxRequests && shouldThrottle(config.url)) {
            pendingRequests++;
            clearInterval(interval);
            resolve(config);
          } else if (!shouldThrottle(config.url)) {
            clearInterval(interval);
            resolve(config);
          }
        }, INTERVAL_MS);
      })
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      if (shouldThrottle(response.config.url)) {
        pendingRequests = Math.max(0, pendingRequests - 1);
      }
      return Promise.resolve(response);
    },
    (error) => {
      if (axios.isAxiosError(error) && shouldThrottle(error.config.url)) {
        pendingRequests = Math.max(0, pendingRequests - 1);
      }
      return Promise.reject(error);
    }
  );
}

module.exports = {
  retry,
  throttle,
};
